import * as React from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionFieldEditor,
  ActionProposalSurface,
  type ActionFieldSpec,
  type ActionFieldValue,
  toast,
  validateField,
} from "@ando/ui";

import { activeActionApi } from "../../hooks/useActiveAction";
import { useProposalExecution } from "../../hooks/useProposalExecution";
import { renderActionTypeIcon } from "../../utils/actionTypeIcon";
import type { ActiveAction } from "../../state/activeActionStore";

/**
 * VoiceReportGenerateCard — HITL action card for the
 * ``voice_report_generate`` action type (Workstream B M2.5, June 17, 2026).
 *
 * Lets the agent propose creating + dispatching a Voice-of-X report from
 * chat and the user EDIT every proposed field before confirming. On
 * confirm the BE creates the report definition and kicks off the first
 * generation run in one round-trip; the resulting report surfaces via
 * the chat ``voice_report`` preview embed (M2.4).
 *
 * Pattern source (DRY — feedback_hitl_card_editable_fields):
 *   - Chrome: shared ``ActionProposalSurface`` (same shell every action
 *     uses — sticky toolbar, status badge, in-surface decision bar).
 *   - Body: schema-driven ``ActionFieldEditor`` — every field editable,
 *     same renderer as email / calendar / FiC / Salesforce.
 *   - Lifecycle: shared ``useProposalExecution`` hook owns the
 *     ``executing → executed | proposed`` patch dance + the audit-log
 *     diff transport (``modifications`` body keyed by field name).
 *
 * Field order honours ``feedback_action_forms_account_picker_first``:
 * the dataset picker is the FIRST field — every other field (title,
 * brief, template) is conceptually dependent on which dataset the user
 * picked. The picker uses the ``reference`` renderer with
 * ``referenceTo="Dataset"`` so it free-text-degrades gracefully while
 * the dedicated lookup endpoint is being landed BE-side.
 *
 * Diff tracking: the proposed values arrive on ``action.proposedFields``
 * (immutable snapshot); the editor holds the current values in local
 * state. On submit we diff key-by-key and ship ONLY the changed keys
 * under ``modifications`` — the BE audit log stores both halves so
 * ``proposed_fields`` ≠ ``executed_fields`` is auditable end-to-end.
 *
 * Language: every label/copy passes through Lingui ``<Trans>`` /
 * ``t\`...\``` (feedback_llm_prompts_mirror_language_never_force — the
 * UI mirrors the user's chosen language, never forces it).
 */

export const VOICE_REPORT_GENERATE_ACTION_TYPE = "voice_report_generate" as const;

/**
 * Static field schema for the ``voice_report_generate`` action.
 *
 * Kept colocated with the card (NOT in the global field-spec catalog) on
 * purpose: the schema is read-once at render time and mirrors the BE
 * ``RunVoiceReportInput`` + ``CreateVoiceReportInput`` body shape one-
 * for-one (``dataset_id``, ``title``, ``brief``, ``template_id``,
 * ``scope_spec`` → ``scope_json``). When the BE ships a dedicated
 * unified-payload-schema entry for this type, this static array will be
 * replaced by ``usePayloadSchema(action.id).editorSchema`` (the same
 * upgrade path every other scalar action takes).
 *
 * Labels are passed verbatim because ``ActionFieldEditor`` renders them
 * outside any Lingui macro context — the card-level ``<Trans>`` strings
 * (header / buttons) are inside the Lingui-aware tree.
 */
function buildVoiceReportFieldSchema(labels: {
  readonly dataset: string;
  readonly datasetPlaceholder: string;
  readonly title: string;
  readonly titlePlaceholder: string;
  readonly brief: string;
  readonly briefPlaceholder: string;
  readonly template: string;
  readonly scope: string;
}): readonly ActionFieldSpec[] {
  return [
    {
      name: "dataset_id",
      label: labels.dataset,
      // ``reference`` typeahead — degrades to a free-text input when no
      // ``loadReferenceOptions`` is wired (the BE dataset-list endpoint
      // lands in a follow-up milestone). Free-text is still editable so
      // the field is never a dead-end.
      type: "reference",
      required: true,
      referenceTo: "Dataset",
      placeholder: labels.datasetPlaceholder,
    },
    {
      name: "title",
      label: labels.title,
      type: "string",
      required: true,
      placeholder: labels.titlePlaceholder,
    },
    {
      name: "brief",
      label: labels.brief,
      type: "text",
      required: true,
      placeholder: labels.briefPlaceholder,
    },
    {
      name: "template_id",
      label: labels.template,
      type: "select",
      required: false,
      // Empty options array → ``<select>`` renders only the "—" placeholder;
      // the BE template catalogue endpoint is wired later and replaces
      // this static list at that point.
      options: [],
    },
    {
      name: "scope_spec",
      label: labels.scope,
      // JSON renderer is read-friendly for the AI-proposed scope (verifier
      // replays the same filter at fact-check time so the citation set
      // stays stable). Editable in case the user wants to widen / narrow
      // the scope before confirming — same audit contract as every other
      // free-text field.
      type: "json",
      required: false,
      placeholder: "{ }",
    },
  ] as const;
}

export interface VoiceReportGenerateCardProps {
  readonly action: ActiveAction;
  /** Fired after Annulla — the caller decides whether to clear ?proposal=. */
  readonly onAfterReject?: () => void;
  /**
   * Fired when the operator clicks "Modifica e salva come bozza" — keeps
   * the proposal in the active-action-store with the edited values
   * persisted (no execute). Default: a toast confirms the save.
   */
  readonly onSaveDraft?: (
    proposalId: string,
    editedFields: Readonly<Record<string, ActionFieldValue>>,
  ) => void;
}

export const VoiceReportGenerateCard: React.FC<VoiceReportGenerateCardProps> = ({
  action,
  onAfterReject,
  onSaveDraft,
}) => {
  const { t, i18n } = useLingui();
  const { execute, busy: executing } = useProposalExecution();

  // ── Schema ────────────────────────────────────────────────────────────
  // Memo so the editor's debounce effect doesn't re-arm on every parent
  // render (same pattern as ``ScalarActionProposalMount``).
  const schema = React.useMemo<readonly ActionFieldSpec[]>(
    () =>
      buildVoiceReportFieldSchema({
        dataset: t`Dataset`,
        datasetPlaceholder: t`Cerca dataset…`,
        title: t`Titolo`,
        titlePlaceholder: t`Voice of X — clienti Q2`,
        brief: t`Brief`,
        briefPlaceholder: t`Cosa vuoi capire da questo report?`,
        template: t`Template (opzionale)`,
        scope: t`Filtro dataset (avanzato)`,
      }),
    [t],
  );

  // ── Diff tracking — proposed (immutable seed) vs current (editor) ─────
  // feedback_hitl_card_editable_fields: the proposed snapshot stays
  // intact on the action; ``editedFields`` is the FE-mutable companion.
  // The reseed effect mirrors ``ScalarActionProposalMount`` — when the
  // proposal id flips (the user navigates to a different proposal in
  // the same surface) the editor state resets cleanly.
  const seed = React.useMemo<Record<string, ActionFieldValue>>(
    () => ({ ...action.proposedFields, ...(action.editedFields ?? {}) }),
    [action.proposedFields, action.editedFields],
  );
  const [values, setValues] = React.useState<Record<string, ActionFieldValue>>(seed);
  React.useEffect(() => {
    setValues(seed);
  }, [seed]);

  // ── Validation ────────────────────────────────────────────────────────
  const errors = React.useMemo(() => {
    const out: Record<string, { message: string } | undefined> = {};
    for (const spec of schema) {
      out[spec.name] = validateField(spec, values[spec.name] ?? null, i18n);
    }
    return out;
  }, [schema, values, i18n]);
  const hasErrors = React.useMemo(
    () => Object.values(errors).some((e) => e !== undefined),
    [errors],
  );

  const busy = executing || action.status === "executing";

  // Local helper — count how many fields the operator changed from the
  // AI-proposed snapshot so the header can surface an "edited" affordance
  // (screen readers also pick this up via ``aria-describedby``).
  const editedFieldCount = React.useMemo(() => {
    let n = 0;
    for (const [k, v] of Object.entries(values)) {
      if (action.proposedFields[k] !== v) n += 1;
    }
    return n;
  }, [values, action.proposedFields]);

  // ── Submit handler ─────────────────────────────────────────────────────
  const handleConfirm = React.useCallback(() => {
    if (hasErrors) {
      toast.error(t`Correggi i campi evidenziati prima di confermare`);
      return;
    }
    // Diff vs proposed — only the changed keys travel as modifications
    // (same audit contract every other scalar action follows). The BE
    // executor merges ``modifications`` into the proposed payload and
    // logs both halves on the action audit row.
    const proposed = action.proposedFields;
    const modifications: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
      if (proposed[k] !== v) modifications[k] = v;
    }
    execute(
      action.id,
      Object.keys(modifications).length > 0 ? modifications : undefined,
      {
        editedFields: values,
        onError: (err) => {
          toast.error(err.message ?? t`Esecuzione azione fallita. Riprova.`);
        },
      },
    );
  }, [action.id, action.proposedFields, execute, hasErrors, t, values]);

  const handleReject = React.useCallback(() => {
    activeActionApi.remove(action.id);
    onAfterReject?.();
  }, [action.id, onAfterReject]);

  const handleSaveDraft = React.useCallback(() => {
    // Persist the edits onto the action WITHOUT executing. Same store
    // contract as the optimistic ``executing`` patch in
    // ``useProposalExecution`` — but we leave the status at ``proposed``
    // so the action keeps its HITL eligibility.
    activeActionApi.patch(action.id, { editedFields: values });
    if (onSaveDraft) {
      onSaveDraft(action.id, values);
    } else {
      toast.success(t`Bozza salvata`);
    }
  }, [action.id, onSaveDraft, t, values]);

  // ── Body ──────────────────────────────────────────────────────────────
  // The editor renders every spec in declaration order — dataset picker
  // FIRST (feedback_action_forms_account_picker_first), then title,
  // brief, template, scope. ``ActionFieldEditor`` already supplies
  // ``role="region"``-equivalent grouping when ``section`` is set; for
  // this card the flat list is correct because every field is part of
  // the same conceptual block ("come generare il report").
  const editedHintId = `voice-report-edited-${action.id}`;
  const body = (
    <div
      className="space-y-4 px-4 py-4"
      role="region"
      aria-labelledby={`voice-report-title-${action.id}`}
      aria-describedby={editedFieldCount > 0 ? editedHintId : undefined}
    >
      {editedFieldCount > 0 ? (
        <p
          id={editedHintId}
          className="inline-flex items-center gap-1.5 text-[11px] text-amber-700"
        >
          <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
          <Trans>
            Hai modificato {editedFieldCount} campo rispetto alla proposta dell'AI
          </Trans>
        </p>
      ) : null}
      <ActionFieldEditor
        schema={schema}
        values={values}
        errors={errors}
        onChange={setValues}
        disabled={busy}
      />
    </div>
  );

  // ── Title — falls back to the AI-proposed title, then a generic copy ──
  const headerTitle =
    typeof values.title === "string" && values.title.trim().length > 0
      ? values.title
      : action.title || t`Genera un Voice of X report`;

  return (
    <div id={`voice-report-title-${action.id}`}>
      <ActionProposalSurface
        icon={renderActionTypeIcon(VOICE_REPORT_GENERATE_ACTION_TYPE)}
        title={headerTitle}
        rationale={
          action.subtitle ?? t`L'AI propone di generare un Voice of X report`
        }
        status={action.status}
        busy={busy}
        confirmDisabled={hasErrors}
        confirmLabel={t`Esegui`}
        onConfirm={handleConfirm}
        onReject={handleReject}
        // Save-draft surface — the surface itself only exposes Confirm /
        // Reject; the "Modifica e salva come bozza" affordance rides
        // inside the body as a secondary footer button so we don't fork
        // ``ActionProposalSurface`` just for this one action type.
        body={
          <div className="flex flex-col">
            {body}
            <div className="flex justify-end px-4 pb-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={busy}
                className="text-[12px] text-ink-3 underline-offset-2 transition-colors hover:text-ink-1 hover:underline disabled:opacity-50"
              >
                <Trans>Modifica e salva come bozza</Trans>
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};
VoiceReportGenerateCard.displayName = "VoiceReportGenerateCard";
